import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Clock, ArrowRight, AlertTriangle, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const two = (n) => String(n).padStart(2, "0");

export default function TrialBanner() {
  const { user } = useAuth();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!user || !user.membership_active || user.membership_plan !== "trial" || !user.membership_expires) return null;

  const diff = Math.max(0, new Date(user.membership_expires) - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const urgent = diff <= 86400000; // <= 1 hari tersisa

  const goMembership = () => document.getElementById("membership")?.scrollIntoView({ behavior: "smooth" });

  const boxes = [
    { v: days, l: "Hari" },
    { v: hours, l: "Jam" },
    { v: mins, l: "Menit" },
    { v: secs, l: "Detik" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="trial-countdown-banner"
      className={`card p-5 sm:p-6 relative overflow-hidden ${urgent ? "border-[var(--danger)]/50 ring-1 ring-[var(--danger)]/40" : "border-[var(--gold)]/40 ring-1 ring-[var(--gold)]/20"}`}
    >
      <div className={`absolute -top-24 -right-16 w-[300px] h-[300px] rounded-full blur-[100px] ${urgent ? "bg-[var(--danger)]/15" : "bg-[var(--gold)]/12"}`} />
      <div className="relative flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {urgent ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--danger)]/15 text-[var(--danger)] text-[11px] font-bold uppercase tracking-wider animate-pulse" data-testid="trial-urgent-badge">
                <AlertTriangle size={12} /> Trial Segera Berakhir
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--gold)]/15 text-gold text-[11px] font-bold uppercase tracking-wider">
                <Sparkles size={12} /> Free Trial Aktif
              </span>
            )}
          </div>
          <p className="font-heading text-xl sm:text-2xl font-extrabold mt-3 leading-tight">
            {urgent ? (
              <>Trial Anda tinggal <span className="text-[var(--danger)]">1 hari lagi</span> — jangan sampai akses terputus.</>
            ) : (
              <>Nikmati akses penuh FAS Trading. <span className="text-gold">Trial Anda sedang berjalan.</span></>
            )}
          </p>
          <p className="text-sm text-[var(--text-secondary)] mt-2 flex items-center gap-1.5">
            <Sparkles size={14} className="text-accent" />
            Upgrade sekarang dan dapat <span className="font-semibold text-accent">diskon 20%</span> — khusus selama trial masih aktif.
          </p>
        </div>

        <div className="flex flex-col items-start lg:items-end gap-4">
          <div className="flex items-center gap-2.5" data-testid="trial-countdown-clock">
            <Clock size={18} className={urgent ? "text-[var(--danger)]" : "text-gold"} />
            {boxes.map((b, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-mono-data text-2xl font-extrabold ${urgent ? "bg-[var(--danger)]/12 text-[var(--danger)]" : "bg-[var(--surface-hover)] text-[var(--text-primary)]"}`}>
                  {two(b.v)}
                </div>
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mt-1">{b.l}</span>
              </div>
            ))}
          </div>
          <button
            onClick={goMembership}
            data-testid="trial-upgrade-btn"
            className={`inline-flex items-center gap-2 h-11 px-6 rounded-xl font-semibold transition-colors ${urgent ? "bg-[var(--danger)] text-white hover:opacity-90" : "bg-[var(--accent)] text-[#07090E] hover:bg-[var(--accent-hover)]"}`}
          >
            Upgrade Sekarang · Hemat 20% <ArrowRight size={17} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
